import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { useModal } from "../context/ModalContext";
import { ShoppingCart, Upload, X, CheckCircle, LogIn } from "lucide-react";

const apiUrl = process.env.REACT_APP_API_URL;

const getUser = () => {
  try {
    const u = localStorage.getItem("user") || sessionStorage.getItem("user");
    return u ? JSON.parse(u) : null;
  } catch { return null; }
};

const getToken = () => localStorage.getItem("token") || sessionStorage.getItem("token");

const FIELDS = ["Storage", "Ram", "Sim", "Color", "Processor", "Size", "Type", "Capacity", "Usage", "Min"];

const EMPTY_FORM = {
  ItemName: "",
  Price: "",
  Condition: "Used",
  Description: "",
  Storage: "",
  Ram: "",
  Sim: "",
  Color: "",
  Processor: "",
  Size: "",
  Min: "",
  Type: "",
  Capacity: "",
  Usage: "",
};

export default function OrderPage() {
  const { showAlert } = useModal();
  const navigate = useNavigate();
  const user = getUser();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const MAX_IMAGES = 6;
  const MAX_IMAGE_SIZE_MB = 5;

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setImages((prev) => {
      const remaining = MAX_IMAGES - prev.length;
      const valid = files.slice(0, remaining).filter((f) => {
        if (f.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
          showAlert(`${f.name} is too large (max ${MAX_IMAGE_SIZE_MB}MB)`, "error");
          return false;
        }
        return true;
      });
      return [...prev, ...valid.map((file) => ({ file, preview: URL.createObjectURL(file) }))];
    });
    e.target.value = "";
  };

  const removeImage = (index) => {
    setImages((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePostOrder = async () => {
    if (loading) return;

    // Guest — redirect to login, return here after
    if (!getToken()) {
      navigate("/login", { state: { from: "/place-order" } });
      return;
    }

    if (!form.ItemName.trim()) {
      showAlert("Item Name is required", "warning");
      return;
    }
    if (!form.Price || isNaN(form.Price) || Number(form.Price) <= 0) {
      showAlert("Please enter a valid target price", "warning");
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      Object.keys(form).forEach((key) => {
        if (form[key]) fd.append(key, form[key]);
      });
      images.forEach((img) => fd.append("images", img.file));

      const res = await axios.post(`${apiUrl}/api/orders`, fd, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (!res.data.success) {
        showAlert(res.data.error || "Something went wrong", "error");
        return;
      }

      setSubmitted(true);
    } catch (err) {
      showAlert(err.response?.data?.error || "Failed to place order", "error");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f4faf7] via-[#fafbfc] to-[#f4f7f5]">
        <Header />
        <div className="max-w-lg mx-auto px-6 py-24 flex flex-col items-center text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-extrabold text-gray-900 mb-3">Order Submitted!</h1>
          <p className="text-gray-500 text-lg mb-8">
            Our team has received your request and will reach out to you shortly to confirm and arrange delivery.
          </p>
          <a
            href="/"
            className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl shadow-lg shadow-green-600/20 transition-all duration-200"
          >
            Back to Home
          </a>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f4faf7] via-[#fafbfc] to-[#f4f7f5]">
      <Header />

      <main className="max-w-2xl mx-auto px-6 py-12">
        {/* Page Header */}
        {/* Guest banner */}
        {!user && (
          <div className="mb-6 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4">
            <LogIn className="w-5 h-5 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800">
              You're browsing as a guest. You'll be asked to{" "}
              <a href="/login" className="font-bold underline hover:text-amber-900">log in</a>{" "}
              before your order is submitted.
            </p>
          </div>
        )}

        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-green-600 text-white rounded-xl flex items-center justify-center shadow-lg shadow-green-600/20">
              <ShoppingCart className="w-5 h-5" />
            </div>
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Place an Order</h1>
          </div>
          <p className="text-gray-500 leading-relaxed ml-13">
            Can't find what you're looking for? Tell us what you need — our team will source it and deliver it to you.
          </p>
        </div>

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
            <h2 className="text-sm font-extrabold text-gray-500 uppercase tracking-wider">Item Details</h2>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Item Name <span className="text-red-500">*</span></label>
              <input
                name="ItemName"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none transition text-gray-900 placeholder-gray-400"
                placeholder="e.g. iPhone 15 Pro Max, Samsung 4K TV..."
                onChange={handleChange}
                value={form.ItemName}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Target Price (ETB) <span className="text-red-500">*</span></label>
                <input
                  name="Price"
                  type="number"
                  min="0"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none transition text-gray-900 placeholder-gray-400"
                  placeholder="e.g. 85000"
                  onChange={handleChange}
                  value={form.Price}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Condition</label>
                <select
                  name="Condition"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none transition text-gray-900"
                  onChange={handleChange}
                  value={form.Condition}
                >
                  <option value="New">Brand New</option>
                  <option value="Used">Used</option>
                  <option value="Refurbished">Refurbished</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description / Requirements</label>
              <textarea
                name="Description"
                rows="4"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none transition text-gray-900 placeholder-gray-400 resize-none"
                placeholder="Describe the item you need, any specific requirements, preferred brand, colour, etc."
                onChange={handleChange}
                value={form.Description}
              />
            </div>
          </div>

          {/* Specifications */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
            <div>
              <h2 className="text-sm font-extrabold text-gray-500 uppercase tracking-wider">Specifications</h2>
              <p className="text-xs text-gray-400 mt-0.5">Fill in only the fields relevant to your item.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {FIELDS.map((field) => (
                <div key={field}>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{field}</label>
                  <input
                    name={field}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none transition text-gray-900 placeholder-gray-300 text-sm"
                    placeholder={`e.g. ${field === "Ram" ? "8GB" : field === "Storage" ? "256GB" : field === "Color" ? "Black" : "..."}`}
                    onChange={handleChange}
                    value={form[field]}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Reference Images */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
            <div>
              <h2 className="text-sm font-extrabold text-gray-500 uppercase tracking-wider">Reference Images</h2>
              <p className="text-xs text-gray-400 mt-0.5">Optional — upload photos of the item you're looking for.</p>
            </div>

            <input type="file" multiple hidden id="order-img-input" accept="image/*" onChange={handleImageUpload} />

            {images.length < MAX_IMAGES && (
              <button
                type="button"
                onClick={() => document.getElementById("order-img-input").click()}
                className="w-full py-4 border-2 border-dashed border-gray-200 hover:border-green-400 hover:bg-green-50/50 rounded-xl text-gray-500 hover:text-green-600 font-semibold transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Upload className="w-5 h-5" />
                Upload Images ({images.length}/{MAX_IMAGES})
              </button>
            )}

            {images.length > 0 && (
              <div className="grid grid-cols-3 gap-3">
                {images.map((img, i) => (
                  <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-gray-100 group shadow-sm">
                    <img src={img.preview} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-200" />
                    <button
                      onClick={() => removeImage(i)}
                      className="absolute top-1.5 right-1.5 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-sm"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <button
            className={`w-full py-4 rounded-2xl text-white font-extrabold text-base transition-all duration-200 flex items-center justify-center gap-2 ${
              loading
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700 shadow-lg shadow-green-600/20 hover:shadow-green-700/30"
            }`}
            onClick={handlePostOrder}
            disabled={loading}
          >
            <ShoppingCart className="w-5 h-5" />
            {loading ? "Submitting your order..." : "Submit Order Request"}
          </button>

          <p className="text-center text-xs text-gray-400">
            Our team will review your request and contact you within 24 hours.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
